import { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import type { ContactAttachment } from "@/lib/contact-submit";

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

function isAllowedFile(file: File) {
  return file.type === "application/pdf" || file.type.startsWith("image/");
}

function formatFileSize(size: number) {
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function readFile(file: File): Promise<ContactAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const separator = result.indexOf(",");
      if (separator === -1) {
        reject(new Error(`Could not read ${file.name}`));
        return;
      }

      resolve({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
        contentBase64: result.slice(separator + 1),
      });
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

type Props = {
  files: ContactAttachment[];
  onChange: (files: ContactAttachment[]) => void;
  disabled?: boolean;
  compact?: boolean;
};

export const AttachmentField = ({ files, onChange, disabled = false, compact = false }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  const onFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    setError("");
    const availableSlots = MAX_FILES - files.length;
    if (selected.length > availableSlots) {
      setError(`Du kan bifoga högst ${MAX_FILES} filer.`);
      return;
    }

    const invalidFile = selected.find((file) => !isAllowedFile(file));
    if (invalidFile) {
      setError("Bifoga endast PDF-filer eller bildfiler.");
      return;
    }

    const oversizedFile = selected.find((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      setError("Varje fil får vara högst 10 MB.");
      return;
    }

    const selectedSize = selected.reduce((total, file) => total + file.size, 0);
    const currentSize = files.reduce((total, file) => total + file.size, 0);
    if (currentSize + selectedSize > MAX_TOTAL_SIZE) {
      setError("Bilagorna får tillsammans vara högst 25 MB.");
      return;
    }

    try {
      onChange([...files, ...(await Promise.all(selected.map(readFile)))]);
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : "Kunde inte läsa filen.");
    }
  };

  const removeFile = (index: number) => {
    onChange(files.filter((_, fileIndex) => fileIndex !== index));
    setError("");
  };

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground hover:text-primary">
          <Paperclip className="h-4 w-4" />
          Bifoga filer
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            onChange={onFilesSelected}
            disabled={disabled || files.length >= MAX_FILES}
            className="sr-only"
          />
        </label>
        <span className="text-xs text-foreground-muted">PDF eller bild · max 5 filer, 10 MB per fil</span>
      </div>

      {files.length ? (
        <ul className="space-y-2" aria-label="Bifogade filer">
          {files.map((file, index) => (
            <li key={`${file.filename}-${index}`} className="flex items-center justify-between gap-3 rounded-sm border border-border bg-background px-3 py-2 text-sm">
              <span className="min-w-0 truncate text-foreground">
                {file.filename} <span className="text-foreground-muted">({formatFileSize(file.size)})</span>
              </span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                disabled={disabled}
                aria-label={`Ta bort ${file.filename}`}
                className="flex-shrink-0 rounded-full p-1 text-foreground-muted hover:bg-background-deep hover:text-foreground disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <p className="text-xs text-red-700" role="alert">{error}</p> : null}
    </div>
  );
};
